import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

const OVERVIEW_FIELDS = [
  "subtitle",
  "subjectOrDiscipline",
  "intendedLearners",
  "educationalLevel",
  "plannedAdaptationOrTranslation",
  "participatingInstitution",
  "curriculumOrProfessionalStandard",
  "editionOrVersion",
];

const FORMAT_PLAN_FIELDS = [
  "trimSize",
  "orientation",
  "bleed",
  "publicationFormat",
];

const TRIM_ORIENTATION = {
  "5.5_x_8.5_in": "portrait",
  "6_x_9_in": "portrait",
  "8.25_x_8.25_in": "square",
  "8.5_x_8.5_in": "square",
  "8_x_10_in": "portrait",
  "8.5_x_11_in": "portrait",
};

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeOverview(value = {}) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  return OVERVIEW_FIELDS.reduce(
    (result, field) => {
      result[field] =
        String(source[field] ?? "");
      return result;
    },
    {}
  );
}

function sameOverview(left, right) {
  return OVERVIEW_FIELDS.every(
    (field) =>
      String(left?.[field] ?? "") ===
      String(right?.[field] ?? "")
  );
}

function normalizeFormatPlan(
  value = {}
) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  const trimSize =
    String(
      source.trimSize ?? ""
    ).trim();

  return {
    trimSize,
    orientation:
      TRIM_ORIENTATION[
        trimSize
      ] ||
      String(
        source.orientation ?? ""
      ).trim(),
    bleed:
      String(
        source.bleed ?? ""
      ).trim(),
    publicationFormat:
      String(
        source.publicationFormat ?? ""
      ).trim(),
  };
}

function sameFormatPlan(
  left,
  right
) {
  return FORMAT_PLAN_FIELDS.every(
    (field) =>
      String(left?.[field] ?? "") ===
      String(right?.[field] ?? "")
  );
}

function SelectField({
  label,
  value,
  onChange,
  disabled,
  options = [],
  guidance = "",
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 7,
      }}
    >
      <span
        style={{
          fontWeight: 800,
        }}
      >
        {label}
      </span>

      {guidance ? (
        <span
          className="ns-card-meta"
          style={{
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          {guidance}
        </span>
      ) : null}

      <select
        className="ns-input"
        value={value}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
      >
        {options.map(
          (option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          )
        )}
      </select>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
  placeholder = "",
  optional = false,
  multiline = false,
  isFR = false,
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 7,
      }}
    >
      <span
        style={{
          fontWeight: 800,
        }}
      >
        {label}
        {optional ? (
          <span
            style={{
              marginLeft: 6,
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {isFR
              ? "(facultatif)"
              : "(optional)"}
          </span>
        ) : null}
      </span>

      {multiline ? (
        <textarea
          className="ns-input"
          rows={3}
          value={value}
          disabled={disabled}
          onChange={(event) =>
            onChange(event.target.value)
          }
          placeholder={placeholder}
          style={{
            resize: "vertical",
            minHeight: 92,
          }}
        />
      ) : (
        <input
          className="ns-input"
          value={value}
          disabled={disabled}
          onChange={(event) =>
            onChange(event.target.value)
          }
          placeholder={placeholder}
        />
      )}
    </label>
  );
}

export default function TextbookProjectOverview({
  project,
  projectPlan,
  loading = false,
  loadError = "",
  onReload,
  onSave,
  onOpenBookStructure,
  canEdit = true,
  isFR = false,
  writingModeLabel = "",
  updatedLabel = "",
}) {
  const persistedOverview =
    useMemo(
      () =>
        normalizeOverview(
          projectPlan?.overview
        ),
      [projectPlan?.overview]
    );

  const persistedFormatPlan =
    useMemo(
      () =>
        normalizeFormatPlan(
          projectPlan?.formatPlan
        ),
      [projectPlan?.formatPlan]
    );

  const [form, setForm] =
    useState(persistedOverview);

  const [
    formatForm,
    setFormatForm,
  ] =
    useState(
      persistedFormatPlan
    );

  const [saving, setSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState("");

  const [saveMessage, setSaveMessage] =
    useState("");

  useEffect(() => {
    setForm(persistedOverview);
    setFormatForm(
      persistedFormatPlan
    );
    setSaveError("");
    setSaveMessage("");
  }, [
    persistedOverview,
    persistedFormatPlan,
  ]);

  const revision =
    Number(projectPlan?.revision || 0);

  /*
   * A Textbook Project Plan is created at revision 1 with the project.
   * Revision 2+ proves that the Owner has explicitly saved the Plan at
   * least once. This gives the writer a clear first-run handoff without
   * adding a second persistence flag or backend field.
   */
  const projectPlanExplicitlySaved =
    Number.isInteger(revision) &&
    revision > 1;

  const dirty =
    !sameOverview(
      form,
      persistedOverview
    ) ||
    !sameFormatPlan(
      formatForm,
      persistedFormatPlan
    );

  const canOpenBookStructure =
    canEdit &&
    !saving &&
    !dirty &&
    projectPlanExplicitlySaved &&
    typeof onOpenBookStructure ===
      "function";

  function updateField(field, value) {
    if (!canEdit) return;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setSaveError("");
    setSaveMessage("");
  }

  function updateFormatField(
    field,
    value
  ) {
    if (!canEdit) return;

    setFormatForm(
      (current) => {
        if (
          field === "trimSize"
        ) {
          return {
            ...current,
            trimSize: value,
            orientation:
              TRIM_ORIENTATION[
                value
              ] || "",
          };
        }

        return {
          ...current,
          [field]: value,
        };
      }
    );

    setSaveError("");
    setSaveMessage("");
  }

  async function submit(event) {
    event?.preventDefault?.();

    if (
      !canEdit ||
      saving ||
      !dirty ||
      revision < 1 ||
      typeof onSave !== "function"
    ) {
      return;
    }

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      await onSave(
        normalizeOverview(form),
        normalizeFormatPlan(
          formatForm
        ),
        revision
      );

      setSaveMessage(
        isFR
          ? "Plan du projet enregistré."
          : "Project Plan saved. Continue with Book Structure."
      );
    } catch (error) {
      setSaveError(
        clean(error?.message) ||
          (
            isFR
              ? "Impossible d’enregistrer le Plan du projet."
              : "Could not save the Project Plan."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  if (
    loading ||
    (!projectPlan && !loadError)
  ) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <strong>
          {isFR
            ? "Ouverture du Plan du projet…"
            : "Opening Project Plan…"}
        </strong>
      </section>
    );
  }

  if (!projectPlan) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <p
          style={{
            marginTop: 0,
            color: "#b91c1c",
            fontWeight: 800,
          }}
        >
          {loadError ||
            (
              isFR
                ? "Le Plan du projet du manuel n’a pas pu être ouvert."
                : "The Textbook Project Plan could not be opened."
            )}
        </p>

        {typeof onReload ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReload}
          >
            {isFR
              ? "Réessayer"
              : "Try again"}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <form
      className="ns-card"
      onSubmit={submit}
      style={{
        padding: 22,
        borderRadius: 20,
        border:
          "1.5px solid rgba(5,150,105,0.24)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 760,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#047857",
            }}
          >
            {isFR
              ? "NEXTSCENES BOOK BLUEPRINT · PLAN DU PROJET"
              : "NEXTSCENES BOOK BLUEPRINT · PROJECT PLAN"}
          </div>

          <h3
            className="ns-card-title ns-textbook-academic-title"
            style={{
              margin: "6px 0 8px",
              fontSize: 26,
            }}
          >
            {clean(project?.title) ||
              (
                isFR
                  ? "Manuel sans titre"
                  : "Untitled Textbook"
              )}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 720,
            }}
          >
            {isFR
              ? "Commencez avec ce que vous savez. Vous pourrez préciser les autres détails à mesure que le livre prend forme."
              : "Start with what you know. You can refine the remaining details as the book takes shape."}
          </p>
        </div>

        <div
          className="ns-card ns-card-soft"
          style={{
            padding: "12px 14px",
            borderRadius: 14,
            minWidth: 210,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              marginBottom: 5,
            }}
          >
            {isFR
              ? "Contexte du projet"
              : "Project context"}
          </div>

          <div
            className="ns-card-meta"
            style={{
              lineHeight: 1.55,
            }}
          >
            {clean(
              project?.primaryLanguage
            ) || "en"}
            {" · "}
            {writingModeLabel ||
              clean(
                project?.writingMode
              ) ||
              "Solo"}
            <br />
            {clean(
              project?.visibility
            ) ||
              "private_draft"}
            {updatedLabel
              ? ` · ${updatedLabel}`
              : ""}
          </div>
        </div>
      </div>

      {!canEdit ? (
        <div
          className="ns-card ns-card-soft"
          role="status"
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 14,
          }}
        >
          <strong>
            {isFR
              ? "Lecture seule"
              : "Read only"}
          </strong>

          <div
            className="ns-card-meta"
            style={{
              marginTop: 4,
            }}
          >
            {isFR
              ? "Seul le propriétaire peut modifier le Plan du projet dans cette version."
              : "Only the project owner can edit the Project Plan in this version."}
          </div>
        </div>
      ) : null}

      <section
        className="ns-card ns-card-soft"
        style={{
          marginTop: 20,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Identité du livre"
            : "Book identity"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Le titre principal est celui utilisé lors de la création du projet. Ajoutez ici les précisions utiles à cette édition."
            : "The main title is the one used when the project was created. Add the details that help identify this edition."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <Field
            label={
              isFR
                ? "Sous-titre"
                : "Subtitle"
            }
            value={form.subtitle}
            onChange={(value) =>
              updateField(
                "subtitle",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Sous-titre du livre"
                : "Book subtitle"
            }
            optional
            isFR={isFR}
          />

          <Field
            label={
              isFR
                ? "Édition ou version"
                : "Edition or version"
            }
            value={
              form.editionOrVersion
            }
            onChange={(value) =>
              updateField(
                "editionOrVersion",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Ex. Première édition"
                : "e.g. First edition"
            }
            optional
            isFR={isFR}
          />
        </div>
      </section>

      <section
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Sujet et apprenants"
            : "Subject and learners"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Décrivez simplement ce que vous enseignez et à qui le livre est destiné."
            : "Describe simply what the book teaches and who it is for."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <Field
            label={
              isFR
                ? "Matière ou discipline"
                : "Subject or discipline"
            }
            value={
              form.subjectOrDiscipline
            }
            onChange={(value) =>
              updateField(
                "subjectOrDiscipline",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Ex. Mathématiques, histoire, ingénierie"
                : "e.g. Mathematics, history, engineering"
            }
            isFR={isFR}
          />

          <Field
            label={
              isFR
                ? "Niveau d’enseignement"
                : "Educational level"
            }
            value={
              form.educationalLevel
            }
            onChange={(value) =>
              updateField(
                "educationalLevel",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Ex. Secondaire, licence, formation professionnelle"
                : "e.g. Secondary, undergraduate, professional training"
            }
            isFR={isFR}
          />

          <Field
            label={
              isFR
                ? "Apprenants visés"
                : "Intended learners"
            }
            value={
              form.intendedLearners
            }
            onChange={(value) =>
              updateField(
                "intendedLearners",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Qui utilisera principalement ce livre ?"
                : "Who will primarily use this book?"
            }
            multiline
            isFR={isFR}
          />
        </div>
      </section>

      <section
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Contexte pédagogique et éditorial"
            : "Educational and publishing context"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Ces informations sont utiles lorsqu’un manuel est lié à une institution, un programme ou une autre édition. Laissez-les vides si elles ne s’appliquent pas."
            : "These details are useful when a textbook is connected to an institution, curriculum, standard, or another edition. Leave them blank when they do not apply."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <Field
            label={
              isFR
                ? "Institution participante"
                : "Participating institution"
            }
            value={
              form.participatingInstitution
            }
            onChange={(value) =>
              updateField(
                "participatingInstitution",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "École, université, organisme…"
                : "School, university, organisation…"
            }
            optional
            isFR={isFR}
          />

          <Field
            label={
              isFR
                ? "Programme ou norme professionnelle"
                : "Curriculum or professional standard"
            }
            value={
              form.curriculumOrProfessionalStandard
            }
            onChange={(value) =>
              updateField(
                "curriculumOrProfessionalStandard",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Programme, syllabus ou norme applicable"
                : "Applicable curriculum, syllabus, or standard"
            }
            optional
            isFR={isFR}
          />

          <Field
            label={
              isFR
                ? "Adaptation ou traduction prévue"
                : "Planned adaptation or translation"
            }
            value={
              form.plannedAdaptationOrTranslation
            }
            onChange={(value) =>
              updateField(
                "plannedAdaptationOrTranslation",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            placeholder={
              isFR
                ? "Ex. Édition anglaise prévue"
                : "e.g. French edition planned"
            }
            optional
            isFR={isFR}
          />
        </div>
      </section>

      <section
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Format et mise en page"
            : "Format & Page Design"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Choisissez ici la direction générale du format. NextScenes gardera les décisions techniques en arrière-plan et vous pourrez encore les affiner avant la publication."
            : "Choose the working format here so writing, figures, and page design can develop toward the same output. You can refine these choices before publication."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <SelectField
            label={
              isFR
                ? "Format de publication"
                : "Publication format"
            }
            value={
              formatForm
                .publicationFormat
            }
            onChange={(value) =>
              updateFormatField(
                "publicationFormat",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Pas encore décidé"
                  : "Not decided yet",
              },
              {
                value: "print",
                label: isFR
                  ? "Imprimé"
                  : "Print",
              },
              {
                value: "digital",
                label: isFR
                  ? "Numérique"
                  : "Digital",
              },
              {
                value:
                  "print_and_digital",
                label: isFR
                  ? "Imprimé et numérique"
                  : "Print and digital",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Format d’impression"
                : "Trim size"
            }
            guidance={
              isFR
                ? "Choisissez un format déjà validé par NextScenes Book Print. Laissez-le indécis pour un projet uniquement numérique ou si la décision viendra plus tard."
                : "Choose a size already validated by NextScenes Book Print. Leave it undecided for a digital-only project or if the print decision comes later."
            }
            value={
              formatForm.trimSize
            }
            onChange={(value) =>
              updateFormatField(
                "trimSize",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Pas encore décidé"
                  : "Not decided yet",
              },
              {
                value:
                  "5.5_x_8.5_in",
                label:
                  "5.5 × 8.5 inches",
              },
              {
                value:
                  "6_x_9_in",
                label:
                  "6 × 9 inches",
              },
              {
                value:
                  "8.25_x_8.25_in",
                label:
                  "8.25 × 8.25 inches",
              },
              {
                value:
                  "8.5_x_8.5_in",
                label:
                  "8.5 × 8.5 inches",
              },
              {
                value:
                  "8_x_10_in",
                label:
                  "8 × 10 inches",
              },
              {
                value:
                  "8.5_x_11_in",
                label:
                  "8.5 × 11 inches",
              },
            ]}
          />

          <div
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              Orientation
            </span>

            <div
              className="ns-input"
              role="status"
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: 42,
                background:
                  "rgba(248,250,252,0.9)",
              }}
            >
              {formatForm.orientation
                ? (
                    formatForm.orientation ===
                    "square"
                      ? (
                          isFR
                            ? "Carré"
                            : "Square"
                        )
                      : "Portrait"
                  )
                : (
                    isFR
                      ? "Choisissez un format d’impression"
                      : "Choose a trim size"
                  )}
            </div>

            <span
              className="ns-card-meta"
              style={{
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              {isFR
                ? "NextScenes règle automatiquement l’orientation correspondant au format validé."
                : "NextScenes sets the matching orientation automatically from the validated trim size."}
            </span>
          </div>

          <SelectField
            label={
              isFR
                ? "Fond perdu"
                : "Bleed"
            }
            guidance={
              isFR
                ? "Pour l’impression : indiquez si le contenu visuel peut atteindre le bord de la page."
                : "For print: decide whether visual content may extend to the page edge."
            }
            value={
              formatForm.bleed
            }
            onChange={(value) =>
              updateFormatField(
                "bleed",
                value
              )
            }
            disabled={
              saving || !canEdit
            }
            options={[
              {
                value: "",
                label: isFR
                  ? "Pas encore décidé"
                  : "Not decided yet",
              },
              {
                value: "yes",
                label: isFR
                  ? "Oui"
                  : "Yes",
              },
              {
                value: "no",
                label: isFR
                  ? "Non"
                  : "No",
              },
            ]}
          />
        </div>

        <div
          className="ns-card-meta"
          style={{
            marginTop: 14,
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Le type de papier et la couleur de l’intérieur restent des décisions de production. Vous les choisirez plus tard dans Préparer pour la publication."
            : "Paper type and interior colour remain production decisions. You will choose them later in Prepare for Publication."}
        </div>
      </section>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 20,
        }}
      >
        {canEdit ? (
          <button
            className="ns-button ns-button-primary"
            type="submit"
            disabled={
              saving ||
              !dirty ||
              revision < 1
            }
            aria-busy={saving}
          >
            {saving
              ? (
                  isFR
                    ? "Enregistrement…"
                    : "Saving…"
                )
              : (
                  isFR
                    ? "Enregistrer le Plan du projet"
                    : "Save Project Plan"
                )}
          </button>
        ) : null}

        {canEdit ? (
          <button
            className={
              canOpenBookStructure
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            type="button"
            data-textbook-edit-book-structure="true"
            disabled={
              !canOpenBookStructure
            }
            onClick={
              onOpenBookStructure
            }
            title={
              canOpenBookStructure
                ? (
                    isFR
                      ? "Continuer vers la Structure du livre"
                      : "Continue to Book Structure"
                  )
                : dirty
                  ? (
                      isFR
                        ? "Enregistrez d’abord les modifications du Plan du projet."
                        : "Save the current Project Plan changes first."
                    )
                  : !projectPlanExplicitlySaved
                    ? (
                        isFR
                          ? "Enregistrez d’abord le Plan du projet."
                          : "Save Project Plan first."
                      )
                    : ""
            }
          >
            {isFR
              ? "Modifier la Structure du livre"
              : "Edit Book Structure"}
          </button>
        ) : null}

        {saveMessage ? (
          <strong
            role="status"
            style={{
              color: "#166534",
              fontSize: 13,
            }}
          >
            {saveMessage}
          </strong>
        ) : null}

        {saveError ? (
          <strong
            role="alert"
            style={{
              color: "#b91c1c",
              fontSize: 13,
            }}
          >
            {saveError}
          </strong>
        ) : null}
      </div>
    </form>
  );
}
